import React, { useState } from 'react';
import type { FlowPackage } from './videoStudioTypes';
import { videoStudioApi } from './videoStudioApi';

interface FlowExportCardProps {
  campaignId: string;
  creativeId: string;
  onVersionImported: (videoUrl: string, makeActive: boolean) => void;
  onError?: (error: string | null) => void;
}

export const FlowExportCard: React.FC<FlowExportCardProps> = ({
  campaignId,
  creativeId,
  onVersionImported,
  onError,
}) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [makeActive, setMakeActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [flowPkg, setFlowPkg] = useState<FlowPackage | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleExportFlow = async () => {
    setIsExporting(true);
    setLocalError(null);
    try {
      const res = await videoStudioApi.exportFlowPackage(campaignId, creativeId);
      if (res.ok && res.package) {
        setFlowPkg(res.package);
      }
    } catch (err: any) {
      const msg = err.message || 'Falha ao compilar pacote do Google Flow';
      setLocalError(msg);
      if (onError) onError(msg);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyJson = () => {
    if (!flowPkg) return;
    navigator.clipboard.writeText(JSON.stringify(flowPkg, null, 2));
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2500);
  };

  const handleDownloadJson = () => {
    if (!flowPkg) return;
    const blob = new Blob([JSON.stringify(flowPkg, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flow-package-${creativeId}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoUrl.trim()) return;

    setIsSubmitting(true);
    setLocalError(null);
    if (onError) onError(null);

    try {
      const res = await videoStudioApi.importVideoVersion(campaignId, creativeId, {
        video_url: videoUrl.trim(),
        thumbnail_url: thumbnailUrl.trim() || null,
        review_notes: reviewNotes.trim() || null,
        is_selected: makeActive,
      });

      if (res.ok) {
        onVersionImported(videoUrl.trim(), makeActive);
        setVideoUrl('');
        setThumbnailUrl('');
        setReviewNotes('');
      } else {
        const msg = 'Não foi possível importar a versão.';
        setLocalError(msg);
        if (onError) onError(msg);
      }
    } catch (err: any) {
      const msg = err.message || 'Erro ao importar versão manual';
      setLocalError(msg);
      if (onError) onError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vstudio-card" style={{ borderStyle: 'dashed', marginTop: '1.5rem', background: '#fafaf9' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <span style={{ fontSize: '1.2rem' }}>📦</span>
            <h5 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>
              Alternativa Manual: Google Flow & Importação Direta
            </h5>
            <span className="vstudio-badge vstudio-badge-secondary">Workflow Manual</span>
          </div>
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary, #64748b)', maxWidth: '580px' }}>
            Prefere montar ou revisar manualmente? Exporte o pacote estruturado com prompts, referências e timing para o Google Flow, ou vincule a URL do vídeo já finalizado.
          </p>
        </div>

        <button
          type="button"
          className="vstudio-btn vstudio-btn-secondary"
          style={{ fontSize: '0.8rem', padding: '0.45rem 0.85rem' }}
          onClick={handleExportFlow}
          disabled={isExporting}
        >
          {isExporting ? 'Exportando...' : '📥 Exportar Pacote Flow'}
        </button>
      </div>

      {localError && (
        <div style={{ padding: '0.5rem 0.75rem', borderRadius: '0.4rem', background: '#fee2e2', color: '#991b1b', marginBottom: '1rem', fontSize: '0.8rem' }}>
          {localError}
        </div>
      )}

      {flowPkg && (
        <div style={{ marginBottom: '1.25rem', padding: '0.75rem', borderRadius: '0.5rem', background: '#fff', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <button
              type="button"
              className="vstudio-btn vstudio-btn-secondary"
              style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem' }}
              onClick={handleCopyJson}
            >
              {copyFeedback ? '✓ Copiado!' : '📋 Copiar JSON'}
            </button>
            <button
              type="button"
              className="vstudio-btn vstudio-btn-outline"
              style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem' }}
              onClick={handleDownloadJson}
            >
              💾 Baixar .json
            </button>
          </div>
          <pre className="vstudio-code-block" style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '0.75rem' }}>
            {JSON.stringify(flowPkg, null, 2)}
          </pre>
        </div>
      )}

      <form onSubmit={handleImport}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="vstudio-form-label">URL Direta do Vídeo Finalizado (MP4)</label>
            <input
              type="url"
              required
              className="vstudio-input"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://exemplo.com/videos/meu-anuncio-flow.mp4"
            />
          </div>

          <div>
            <label className="vstudio-form-label">URL da Thumbnail / Capa (Opcional)</label>
            <input
              type="url"
              className="vstudio-input"
              value={thumbnailUrl}
              onChange={(e) => setThumbnailUrl(e.target.value)}
              placeholder="https://exemplo.com/capa.jpg"
            />
          </div>

          <div>
            <label className="vstudio-form-label">Anotações / Versão Flow</label>
            <input
              type="text"
              className="vstudio-input"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Ex: Montado manualmente via Flow Pilot com áudio ajustado"
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.825rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={makeActive}
              onChange={(e) => setMakeActive(e.target.checked)}
            />
            <span>Definir imediatamente como vídeo oficial do anúncio</span>
          </label>

          <button
            type="submit"
            className="vstudio-btn vstudio-btn-primary"
            style={{ fontSize: '0.825rem', padding: '0.45rem 1rem' }}
            disabled={isSubmitting || !videoUrl.trim()}
          >
            {isSubmitting ? 'Importando...' : '📥 Vincular Vídeo Flow'}
          </button>
        </div>
      </form>
    </div>
  );
};
